import React, { useState } from 'react';
import { Setlist } from '../types/setlist';

interface Props {
  setlists: Setlist[];
  artistName: string;
  excludedIds?: Set<string>;
  onToggleExclude?: (id: string) => void;
}

export const SetlistDisplay: React.FC<Props> = ({
  setlists,
  artistName,
  excludedIds = new Set(),
  onToggleExclude = () => {},
}) => {
  const [expanded, setExpanded] = useState<string | null>(null);
  const [showAllExcludedWarning, setShowAllExcludedWarning] = useState(false);

  if (setlists.length === 0) {
    return (
      <div style={{ padding: '16px', color: '#666', textAlign: 'center' }}>
        No setlists found for {artistName}.
      </div>
    );
  }

  const handleToggleExclude = (id: string) => {
    const isCurrentlyExcluded = excludedIds.has(id);
    if (!isCurrentlyExcluded) {
      const includedCount = setlists.filter(s => !excludedIds.has(s.id)).length;
      if (includedCount <= 1) {
        setShowAllExcludedWarning(true);
        return;
      }
    }
    setShowAllExcludedWarning(false);
    onToggleExclude(id);
  };

  return (
    <div>
      <h3 style={{ margin: '0 0 12px', color: '#333' }}>Recent Setlists for {artistName}</h3>
      {showAllExcludedWarning && (
        <div
          role="alert"
          style={{
            marginBottom: '10px',
            padding: '8px 12px',
            background: '#fff8e1',
            border: '1px solid #ffe082',
            borderRadius: '6px',
            color: '#7a5c00',
            fontSize: '13px',
          }}
        >
          At least one setlist must be included in the analysis.
        </div>
      )}
      {setlists.map(setlist => {
        const date = setlist.eventDate
          ? new Date(setlist.eventDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
          : 'Unknown date';
        const location = [setlist.venue, setlist.city, setlist.country].filter(Boolean).join(', ');
        const isExpanded = expanded === setlist.id;
        const isExcluded = excludedIds.has(setlist.id);

        return (
          <div
            key={setlist.id}
            style={{
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              marginBottom: '10px',
              backgroundColor: isExcluded ? '#f9f9f9' : '#fff',
              overflow: 'hidden',
              opacity: isExcluded ? 0.65 : 1,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'stretch' }}>
              <button
                onClick={() => setExpanded(isExpanded ? null : setlist.id)}
                aria-expanded={isExpanded}
                aria-label={`${date} at ${location || 'Unknown venue'}`}
                style={{
                  flex: 1,
                  textAlign: 'left',
                  padding: '12px 16px',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <div>
                  <div style={{ fontWeight: 'bold', color: isExcluded ? '#aaa' : '#1DB954' }}>{date}</div>
                  <div style={{ color: '#555', fontSize: '14px' }}>{location || 'Unknown venue'}</div>
                  {setlist.tour && <div style={{ color: '#888', fontSize: '12px' }}>{setlist.tour}</div>}
                </div>
                <span style={{ color: '#aaa', fontSize: '18px' }}>{isExpanded ? '▲' : '▼'}</span>
              </button>
            </div>
            {isExpanded && (
              <div>
                <div style={{ padding: '8px 16px', borderTop: '1px solid #f0f0f0', borderBottom: '1px solid #f0f0f0' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', color: '#666' }}>
                    <input
                      type="checkbox"
                      checked={isExcluded}
                      onChange={() => handleToggleExclude(setlist.id)}
                      aria-label={`Exclude ${date} setlist from analysis`}
                    />
                    Exclude from analysis
                  </label>
                </div>
                <ol style={{ margin: '0', padding: '0 16px 12px 32px', color: '#333', fontSize: '14px' }}>
                  {setlist.sets.length === 0
                    ? <li style={{ listStyle: 'none', color: '#888' }}>No song data available</li>
                    : setlist.sets.map((song, i) => (
                        <li key={i} style={{ padding: '2px 0' }}>
                          {song.name}
                          {song.isCover && <span style={{ color: '#888', marginLeft: '6px', fontSize: '11px' }}>(cover)</span>}
                          {song.isTape && <span style={{ color: '#888', marginLeft: '6px', fontSize: '11px' }}>(tape)</span>}
                        </li>
                      ))
                  }
                </ol>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
